// Fullscreen portfolio lightbox.
// Click a photo on a service page → opens here in immersion mode.
//   - Black/blur backdrop, photo centered, native aspect ratio
//   - ◀ / ▶ navigation: keyboard arrows, on-screen buttons, mobile swipe
//   - Escape or backdrop click to close
//   - Cycles (last → first) for a continuous browsing feel
//
// Rendered via portal so it can escape any parent z-index/overflow trap.
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const SWIPE_THRESHOLD_PX = 50;

const Lightbox = ({ items, index, onClose, onPrev, onNext, serviceSlug }) => {
  const { lang } = useLanguage();
  const open = index !== null && index !== undefined && items && items[index];

  // Keyboard navigation (arrows + escape). Listener is bound only while open.
  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext],
  );

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("keydown", handleKey);
    // Prevent background scroll while immersed.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKey]);

  // Touch swipe for mobile. Tracks horizontal delta, fires prev/next past the
  // threshold. Vertical swipes are ignored so users can still close by tapping.
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (dx > 0) onPrev();
    else onNext();
  };

  if (!open) return null;

  const item = items[index];
  const isVideo = item.media_type === "video";
  const total = items.length;
  // Prefer the high-res display URL (item.url) over the thumb for fullscreen.
  const src = item.url || item.thumb_url;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="lightbox-root"
        data-testid="lightbox-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={onClose}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050505]/95 backdrop-blur-md"
      >
        {/* Counter */}
        <div
          className="absolute top-5 left-6 text-[10px] uppercase tracking-[0.32em] text-[#F2EBE5]/70 select-none z-10"
          data-testid="lightbox-counter"
        >
          {index + 1} / {total}
        </div>

        {/* Close */}
        <button
          type="button"
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          data-testid="lightbox-close"
          className="absolute top-4 right-4 md:top-5 md:right-6 w-11 h-11 flex items-center justify-center text-[#F2EBE5] hover:text-[#C5A059] transition-colors z-10"
        >
          <X className="w-6 h-6" strokeWidth={1.5} />
        </button>

        {/* Prev */}
        {total > 1 && (
          <button
            type="button"
            aria-label="Previous"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            data-testid="lightbox-prev"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center text-[#F2EBE5] hover:text-[#C5A059] transition-colors z-10"
          >
            <ChevronLeft className="w-8 h-8 md:w-9 md:h-9" strokeWidth={1.25} />
          </button>
        )}

        {/* Next */}
        {total > 1 && (
          <button
            type="button"
            aria-label="Next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            data-testid="lightbox-next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center text-[#F2EBE5] hover:text-[#C5A059] transition-colors z-10"
          >
            <ChevronRight className="w-8 h-8 md:w-9 md:h-9" strokeWidth={1.25} />
          </button>
        )}

        {/* Media — its click is stopped so tapping the photo itself doesn't
            close the overlay. Tapping outside (backdrop) closes. */}
        <motion.div
          key={item.id || index}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative px-4 md:px-16 max-w-full max-h-full flex items-center justify-center"
        >
          {isVideo ? (
            <video
              src={src}
              data-testid="lightbox-video"
              controls
              autoPlay
              playsInline
              className="max-w-[92vw] max-h-[88vh] block"
            />
          ) : (
            <img
              src={src}
              alt={item.caption || ""}
              data-testid="lightbox-image"
              className="max-w-[92vw] max-h-[88vh] object-contain block"
              draggable={false}
            />
          )}
        </motion.div>

        {/* Optional caption — positioned ABOVE the CTA so it doesn't clash. */}
        {item.caption && (
          <div className="absolute bottom-20 md:bottom-24 left-1/2 -translate-x-1/2 max-w-[90vw] text-center text-sm text-[#F2EBE5]/70 italic font-serif-display px-4 z-10">
            {item.caption}
          </div>
        )}

        {/* Conversion CTA — "Book this shoot" with the photo's service
            pre-selected in the contact form. Stopping propagation here so
            the user can tap the button without the backdrop closing the
            lightbox first. */}
        {serviceSlug && (
          <Link
            to={`/contact?service=${serviceSlug}`}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            data-testid="lightbox-cta"
            className="absolute bottom-5 md:bottom-7 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 px-5 py-3 md:px-7 md:py-3.5 bg-[#C5A059] hover:bg-[#d4b06a] text-[#050505] text-[10px] md:text-[11px] uppercase tracking-[0.3em] font-medium transition-colors shadow-[0_8px_30px_-6px_rgba(197,160,89,0.55)]"
          >
            {lang === "fr" ? "Commander ce shooting" : "Book this shoot"}
            <ArrowUpRight className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={1.75} />
          </Link>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default Lightbox;
